import type { CSSProperties } from "react";
import type { SortableListMarkerStyle, SortableListVariant } from "../ui/types";

const DEFAULT_LIST_INDENT = "1.25rem";

const DEFAULT_MARKER_STYLE_BY_VARIANT: Record<
	SortableListVariant,
	SortableListMarkerStyle
> = {
	ol: "decimal",
	ul: "disc",
};

const MARKER_STYLE_SET_BY_VARIANT: Record<
	SortableListVariant,
	Set<SortableListMarkerStyle>
> = {
	ol: new Set([
		"decimal",
		"lower-alpha",
		"upper-alpha",
		"lower-roman",
		"upper-roman",
		"none",
	]),
	ul: new Set(["disc", "circle", "square", "check", "dash", "arrow", "none"]),
};

/**
 * Glyph markers have no native `list-style-type` keyword, so they are emitted as
 * a CSS string literal (`list-style-type: "✓  "`). Trailing spaces are the gap
 * between marker and text — `::marker` ignores margin.
 */
const GLYPH_MARKER_CSS: Partial<Record<SortableListMarkerStyle, string>> = {
	arrow: '"\\2192  "',
	check: '"\\2713  "',
	dash: '"\\2014  "',
};

/** CSS value for a marker style — the keyword itself, or a glyph string literal. */
export function sortableListMarkerCssValue(
	markerStyle: SortableListMarkerStyle
): string {
	return GLYPH_MARKER_CSS[markerStyle] ?? markerStyle;
}

function isMeaningfulPadding(raw: unknown): boolean {
	if (typeof raw !== "string") {
		return false;
	}
	const t = raw.trim();
	return (
		t !== "" &&
		t !== "0" &&
		t !== "0px" &&
		t !== "0rem" &&
		t !== "0em" &&
		t !== "0%"
	);
}

function clampOpacity(opacityPct: number): number {
	return Math.max(0, Math.min(100, Math.round(opacityPct)));
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
	const trimmed = hex.trim();
	const match = /^#?([a-f\d]{6})$/i.exec(trimmed);
	if (!match) {
		return null;
	}
	const value = match[1];
	return {
		r: Number.parseInt(value.slice(0, 2), 16),
		g: Number.parseInt(value.slice(2, 4), 16),
		b: Number.parseInt(value.slice(4, 6), 16),
	};
}

/**
 * Tailwind / shadcn preflight removes list markers on `ul` / `ol`. Merge these
 * semantics so builder canvas and public preview show bullets or numbers.
 */
export function normalizeSortableListMarkerStyle(
	listVariant: SortableListVariant,
	markerStyle: string | undefined
): SortableListMarkerStyle {
	if (
		typeof markerStyle === "string" &&
		MARKER_STYLE_SET_BY_VARIANT[listVariant].has(
			markerStyle as SortableListMarkerStyle
		)
	) {
		return markerStyle as SortableListMarkerStyle;
	}
	return DEFAULT_MARKER_STYLE_BY_VARIANT[listVariant];
}

export function normalizeSortableListMarkerOpacity(value: unknown): number {
	if (typeof value === "number" && Number.isFinite(value)) {
		return clampOpacity(value);
	}
	if (typeof value === "string" && value.trim() !== "") {
		const parsed = Number.parseFloat(value);
		if (!Number.isNaN(parsed)) {
			return clampOpacity(parsed);
		}
	}
	return 100;
}

export function sortableListMarkerColorCss(
	markerColor: string | undefined,
	markerOpacity: number | undefined
): string | undefined {
	if (!(typeof markerColor === "string" && markerColor.trim() !== "")) {
		return undefined;
	}
	const rgb = hexToRgb(markerColor);
	if (!rgb) {
		return markerColor.trim();
	}
	const opacityPct = normalizeSortableListMarkerOpacity(markerOpacity);
	if (opacityPct >= 100) {
		return `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;
	}
	const alpha = Math.round((opacityPct / 100) * 1000) / 1000;
	return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
}

/** Marker colour is NOT set here — it goes on the `<li>` via `sortableListItemMarkerStyle`. */
export function mergeSortableListSemanticStyles(
	base: CSSProperties,
	listVariant: SortableListVariant,
	markerStyle?: string
): CSSProperties {
	const resolvedMarkerStyle = normalizeSortableListMarkerStyle(
		listVariant,
		markerStyle
	);
	const { color: textColor, ...rest } = base;
	const out: CSSProperties = {
		...rest,
		margin: 0,
		color: typeof textColor === "string" ? textColor : undefined,
		listStyleType: sortableListMarkerCssValue(resolvedMarkerStyle),
		/* outside = hanging indent: wrapped lines align with text, not under the marker */
		listStylePosition: "outside",
	};
	if (
		!(isMeaningfulPadding(out.paddingLeft) || isMeaningfulPadding(out.padding))
	) {
		// A markerless list has nothing to hang, so it should not be indented.
		out.paddingLeft =
			resolvedMarkerStyle === "none" ? "0" : DEFAULT_LIST_INDENT;
	}
	return out;
}

/** Goes on the `<li>`, not the list: typography CSS re-emits the list's own `color` as `!important`. */
export function sortableListItemMarkerStyle(
	markerColor?: string,
	markerOpacity?: number
): CSSProperties {
	const css = sortableListMarkerColorCss(markerColor, markerOpacity);
	return css ? { color: css } : {};
}

export function sortableListItemTextStyle(base: CSSProperties): CSSProperties {
	return typeof base.color === "string" && base.color.trim() !== ""
		? { color: base.color.trim() }
		: {};
}
